import moment from 'moment';
import _ from 'lodash';
import { keycloak, KeycloakRoles } from '@coverright/shared/keycloak';
import { AssignedTagViewOutput, Status, StatusDto } from '@coverright/data-access/types/enrollment';
import { Color } from '@coverright/utils';
import momenttz from "moment-timezone";

// A function, not a const: must be evaluated at call/render time. As a module-level
// const it would be frozen at import - before Keycloak finishes init - and read false.
export const isAgentManager = () =>
  !!keycloak?.realmAccess?.roles?.includes(KeycloakRoles.ROLE_AGENT_MANAGER)

export const colorByStatus = (status?: StatusDto | Status | null): string => {
  switch(status) {
    case StatusDto.ApplicationSubmitted:
      return '#27ad50';
    case StatusDto.Disenrolled:
    case StatusDto.Disqualified:
      return '#DD3D3D';
    case StatusDto.NotYetEngaged: return '#398ed5';
    case StatusDto.SalesQualified:
    case StatusDto.FollowUp: return '#ed7d31';
    case StatusDto.Test:
    case StatusDto.OutOfService:
    case StatusDto.Duplicated: return '#44546a';
    default: return '#FFB800';
  }
}

export const labelByStatus = (status?: StatusDto | Status | null): string => {
  switch(status) {
    case StatusDto.Draft: return 'Draft';
    case StatusDto.NotYetEngaged: return 'Not yet engaged';
    case StatusDto.ApplicationSubmitted: return 'Application submitted';
    case StatusDto.Attempted: return 'Attempted';
    case StatusDto.Contacted: return 'Contacted';
    case StatusDto.Disqualified: return 'Disqualified';
    case StatusDto.SalesQualified: return 'Sales Qualified Lead';
    case StatusDto.Disenrolled: return 'Disenrolled / Churned / Withdrawn';
    case StatusDto.FollowUp: return 'Follow up';
    case StatusDto.OutOfService: return 'Out of service';
    case StatusDto.Duplicated: return 'Duplicated';
    case StatusDto.Test: return 'Test';
    default: return 'Untouched';
  }
}

export const getTagLabel = (tag?: AssignedTagViewOutput): string => {
  if (!tag) {
    return '';
  }
  if (tag.expiresAt) {
    return tag.tag.name + ' in ' + Math.ceil(moment.duration(moment(tag.expiresAt).diff(moment())).asDays()) + ' days';
  }
  return _.upperFirst(tag.tag.name.toLowerCase());
}

export function calcDurationToNow(time?: string) {
  if (!time) {
    return '-';
  }

  const f = (v: number) => v < 10 ? '0' + v : v;

  const duration = Math.abs(moment.duration(moment().diff(moment(time))).asMilliseconds())
  const s = Math.floor( (duration/1000) % 60 );
  const m = Math.floor( (duration/1000/60) % 60 );
  const h = Math.floor( (duration/(1000*60*60)) % 24 );
  const d = Math.floor( duration/(1000*60*60*24) );
  let t = f(h) + ':' + f(m) + ':' + f(s);
  if (d > 0) {
    t = d + 'd ' + t;
  }
  return t;
}

export const letterToColorfulHex = Color.letterToColorfulHex;
export const lightenHexColor = Color.lightenHexColor;
export const darkenHexColor = Color.darkenHexColor;

export const getTimeInState = (state: string, time?: any) => {
  let zone = momenttz().zoneName();

  const calcZone = getStateTimeZone(state);
  if (calcZone) {
    zone = calcZone;
  }

  const result = momenttz(time);
  return result.tz(zone).format('h:mmA');
}

export const getStateTimeZone = (state: string) => {
  const zones: {[key: string]: string[]} = {
    'America/New_York': ["FL", "GA", "SC", "NC", "KY", "VA", "WV", "DC", "MD", "DE", "NJ", "PA", "OH", "IN", "MI", "NY", "CT", "RI", "MA", "NH", "VT", "ME"],
    'America/Chicago': ["TX", "OK", "LA", "AR", "MS", "AL", "TN", "MO", "KS", "IL", "IA", "NE", "WI", "MN", "SD", "ND"],
    'America/Denver': ["AZ", "NM", "CO", "UT", "WY", "ID", "MT"],
    'America/Los_Angeles': ["CA", "NV", "OR", "WA"],
    'America/Anchorage': ["AK"],
    'US/Hawaii': ["HI"],
  };

  for (const zone of Object.keys(zones)) {
    if (zones[zone].includes(state)) {
      return zone;
    }
  }
}
